import type { SearchResponseData } from '@kimuno/api-sdk/lib/types/responses/Common';
import type {
	CourseMindItemSearchResult,
	MindItemSearchResult,
	Topic,
} from '@kimuno/api-sdk/lib/types/responses/MindItem';
import type { ProductSearchResult } from '@kimuno/api-sdk/lib/types/responses/Product';
import type {
	TeachingFormatListResponse,
	WorkloadListResponse,
} from '@kimuno/api-sdk/lib/types/responses/Text';

import type { Pagination, SearchFilters } from '../types/search';
import type { AsyncAction, SyncAction } from './common.actionTypes';

export enum SEARCH_MIND_ITEMS {
	START = 'SEARCH_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_MIND_ITEMS_ERROR',
}

export enum SEARCH_VIDEO_MIND_ITEMS {
	START = 'SEARCH_VIDEO_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_VIDEO_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_VIDEO_MIND_ITEMS_ERROR',
}

export enum SEARCH_COURSE_MIND_ITEMS {
	START = 'SEARCH_COURSE_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_COURSE_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_COURSE_MIND_ITEMS_ERROR',
}

export enum SEARCH_PRODUCTS {
	START = 'SEARCH_PRODUCTS_START',
	SUCCESS = 'SEARCH_PRODUCTS_SUCCESS',
	ERROR = 'SEARCH_PRODUCTS_ERROR',
}

export enum SEARCH_UNIFIED {
	START = 'SEARCH_UNIFIED_START',
	SUCCESS = 'SEARCH_UNIFIED_SUCCESS',
	ERROR = 'SEARCH_UNIFIED_ERROR',
}

export enum SEARCH_TOPICS {
	START = 'SEARCH_TOPICS_START',
	SUCCESS = 'SEARCH_TOPICS_SUCCESS',
	ERROR = 'SEARCH_TOPICS_ERROR',
}

export enum FETCH_WORKLOADS {
	START = 'FETCH_WORKLOADS_START',
	SUCCESS = 'FETCH_WORKLOADS_SUCCESS',
	ERROR = 'FETCH_WORKLOADS_ERROR',
}

export enum FETCH_TEACHING_FORMATS {
	START = 'FETCH_TEACHING_FORMATS_START',
	SUCCESS = 'FETCH_TEACHING_FORMATS_SUCCESS',
	ERROR = 'FETCH_TEACHING_FORMATS_ERROR',
}

export const SET_RESULTS_SEARCH_ID = 'SET_RESULTS_SEARCH_ID';

// `searchId` is the search journey the request belongs to, read from session storage at
// dispatch time. The store keeps it so a consumer can tell whether the results it is
// looking at belong to the journey that is current *now* — the search bar mints the next
// id before the request for it goes out, so agreement on filters alone cannot tell.
type SearchActionPayload = {
	filters: SearchFilters;
	pagination: Pagination;
	searchId?: string;
};

export type SearchMindItemsAction = AsyncAction<
	SEARCH_MIND_ITEMS.START,
	SEARCH_MIND_ITEMS.SUCCESS,
	SEARCH_MIND_ITEMS.ERROR,
	SearchResponseData<MindItemSearchResult>,
	SearchActionPayload
>;

export type SearchVideoMindItemsAction = AsyncAction<
	SEARCH_VIDEO_MIND_ITEMS.START,
	SEARCH_VIDEO_MIND_ITEMS.SUCCESS,
	SEARCH_VIDEO_MIND_ITEMS.ERROR,
	SearchResponseData<MindItemSearchResult>,
	SearchActionPayload
>;

export type SearchProductsAction = AsyncAction<
	SEARCH_PRODUCTS.START,
	SEARCH_PRODUCTS.SUCCESS,
	SEARCH_PRODUCTS.ERROR,
	SearchResponseData<ProductSearchResult>,
	SearchActionPayload
>;

export type SearchCourseMindItemsAction = AsyncAction<
	SEARCH_COURSE_MIND_ITEMS.START,
	SEARCH_COURSE_MIND_ITEMS.SUCCESS,
	SEARCH_COURSE_MIND_ITEMS.ERROR,
	SearchResponseData<CourseMindItemSearchResult>,
	SearchActionPayload
>;

export type SearchUnifiedAction = AsyncAction<
	SEARCH_UNIFIED.START,
	SEARCH_UNIFIED.SUCCESS,
	SEARCH_UNIFIED.ERROR,
	SearchResponseData<MindItemSearchResult | ProductSearchResult>,
	SearchActionPayload
>;

// Results fetched before any journey existed (a fresh tab opened straight on a search
// URL) carry no id, so SearchResults mints one when it reports on them and stamps it
// here — leaving it unset would give the next search no older id to disagree with.
export type SetResultsSearchIdAction = SyncAction<
	typeof SET_RESULTS_SEARCH_ID,
	undefined,
	{ searchId: string; isCourse: boolean }
>;

export type SearchTopicsAction = AsyncAction<
	SEARCH_TOPICS.START,
	SEARCH_TOPICS.SUCCESS,
	SEARCH_TOPICS.ERROR,
	Topic[],
	{ subject: string }
>;

export type FetchWorkloadsAction = AsyncAction<
	FETCH_WORKLOADS.START,
	FETCH_WORKLOADS.SUCCESS,
	FETCH_WORKLOADS.ERROR,
	WorkloadListResponse
>;

export type FetchTeachingFormatsAction = AsyncAction<
	FETCH_TEACHING_FORMATS.START,
	FETCH_TEACHING_FORMATS.SUCCESS,
	FETCH_TEACHING_FORMATS.ERROR,
	TeachingFormatListResponse
>;
